/**
 * Site-wide DOM translator.
 *
 * Why this exists: hand-wiring <T> into every component across 100+ React
 * files would take days. Instead, this module walks the rendered DOM after
 * React commits, finds text nodes whose EXACT trimmed content matches a
 * dictionary key, and replaces them in-place with the translation.
 *
 * Net effect: any English string in src/i18n/dictionary.js is automatically
 * translated wherever it appears on the page, without needing to wire <T>
 * into the source component. The <T> wrapper still works for components
 * where we want explicit control (Header, Hero already use it) — both
 * mechanisms coexist.
 *
 * How it works:
 *   1. On lang change to non-EN, walk document.body recursively, visiting
 *      every Text node. If a node's trimmed text is a dictionary key, swap
 *      it for the translated value. Remember the original in a WeakMap so
 *      we can revert on lang=en.
 *   2. Install a MutationObserver that runs the same translation on:
 *        • New nodes added (React renders new content)
 *        • Text content of existing nodes changed (React updates)
 *      This catches dynamically-rendered content, route changes (SPA), and
 *      modals/popups that appear after the initial walk.
 *   3. On lang=en, disconnect the observer and restore originals from the
 *      WeakMap so the page goes back to the original React-rendered DOM.
 *   4. Tracking-flag guard prevents the observer firing on our own writes
 *      (would otherwise cause an infinite loop).
 *
 * Performance: WeakMap lookup is O(1), dictionary lookup is O(1), text-node
 * walking is O(N) where N = visible text nodes (typically 50-300 per page).
 * Total per-render cost is a few ms on a modern device — imperceptible.
 *
 * Caveats:
 *   • Only translates text nodes whose ENTIRE trimmed content is a
 *     dictionary key. Partial sentences need to be added in full, OR the
 *     component needs to split them into <T>-wrapped parts (as Hero does).
 *   • Skips <script>, <style>, <noscript>, <code>, <pre>, and any element
 *     marked translate="no" or .notranslate.
 *   • Does NOT translate attributes (alt, title, placeholder, aria-label).
 *     Components needing this should use the useT() hook.
 */

import { translateText } from './dictionary';
import { getCachedTranslation, translateAsync, setActiveLanguage, preloadTranslations } from './aiTranslator';

const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE',
  // Form elements — translating their text content (option labels etc.)
  // can break form submission keyed by exact value.
  'TEXTAREA', 'INPUT',
]);

let observer = null;
let activeLang = 'en';
/** original english text per text node — so we can revert on lang=en. */
const originalTexts = new WeakMap();
/** mark nodes we've just written to, so the observer skips them and we
 *  don't loop forever. */
const recentlyWritten = new WeakSet();

function shouldSkip(node) {
  let el = node.parentNode;
  while (el && el !== document.body) {
    if (el.nodeType !== Node.ELEMENT_NODE) {
      el = el.parentNode;
      continue;
    }
    if (SKIP_TAGS.has(el.nodeName)) return true;
    if (el.getAttribute && (el.getAttribute('translate') === 'no' || el.classList.contains('notranslate'))) {
      return true;
    }
    el = el.parentNode;
  }
  return false;
}

function applyTranslationToNode(node, translation) {
  if (!translation) return;
  const original = originalTexts.get(node) ?? node.nodeValue;
  const trimmed = (original || '').trim();
  if (translation === trimmed) return;
  const leading = (original.match(/^\s*/) || [''])[0];
  const trailing = (original.match(/\s*$/) || [''])[0];
  const newValue = `${leading}${translation}${trailing}`;
  if (node.nodeValue === newValue) return;
  if (!originalTexts.has(node)) originalTexts.set(node, original);
  recentlyWritten.add(node);
  node.nodeValue = newValue;
}

function translateTextNode(node) {
  if (!node || node.nodeType !== Node.TEXT_NODE) return;
  if (recentlyWritten.has(node)) {
    // Our own write — clear flag and skip.
    recentlyWritten.delete(node);
    return;
  }
  if (shouldSkip(node)) return;

  const original = originalTexts.get(node) ?? node.nodeValue;
  const trimmed = (original || '').trim();
  if (!trimmed || trimmed.length < 2) return;

  // Skip number/phone/price-only strings (no Latin letters). Our source copy
  // is always English, so a real translatable phrase always contains letters.
  // This stops the translator mangling things like "(646) 656-9717" → "656 a
  // 9717" or "$35" in non-EN languages.
  if (!/[A-Za-z]/.test(trimmed)) return;

  // 1. Try hand-curated dictionary first — always highest quality.
  const fromDict = translateText(trimmed, activeLang);
  if (fromDict && fromDict !== trimmed) {
    applyTranslationToNode(node, fromDict);
    return;
  }

  // 2. Try AI translation cache (memory + localStorage) — sync, instant.
  const fromAiCache = getCachedTranslation(trimmed, activeLang);
  if (fromAiCache && fromAiCache !== trimmed) {
    applyTranslationToNode(node, fromAiCache);
    return;
  }

  // 3. Queue for async AI translation. The Lingva-backed API auto-batches
  //    queued requests and caches results in D1 + localStorage so this is
  //    a one-time cost per (text, language) pair across the entire user
  //    base. The text shows in English for a brief moment, then swaps in
  //    once the translation arrives (typically 100-300ms).
  translateAsync(trimmed, activeLang).then((translated) => {
    if (translated && translated !== trimmed && activeLang !== 'en') {
      // Re-apply only if the user hasn't switched languages while waiting.
      applyTranslationToNode(node, translated);
    }
  });
}

function walkAndTranslate(root) {
  if (!root) return;
  // SHOW_TEXT only — fast, ignores element nodes.
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const batch = [];
  let n = walker.nextNode();
  while (n) {
    batch.push(n);
    n = walker.nextNode();
  }
  // Translate after collection to avoid the walker getting confused by
  // text node value changes mid-walk.
  for (let i = 0; i < batch.length; i++) {
    translateTextNode(batch[i]);
  }
}

function restoreAllOriginals(root) {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let n = walker.nextNode();
  while (n) {
    if (originalTexts.has(n)) {
      recentlyWritten.add(n);
      n.nodeValue = originalTexts.get(n);
    }
    n = walker.nextNode();
  }
}

function handleMutations(mutations) {
  if (activeLang === 'en') return; // nothing to do, observer should be off
  for (let i = 0; i < mutations.length; i++) {
    const m = mutations[i];
    if (m.type === 'childList') {
      for (let j = 0; j < m.addedNodes.length; j++) {
        const node = m.addedNodes[j];
        if (node.nodeType === Node.TEXT_NODE) {
          translateTextNode(node);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          walkAndTranslate(node);
        }
      }
    } else if (m.type === 'characterData') {
      translateTextNode(m.target);
    }
  }
}

/**
 * Switch the page to a target language. Walks the entire DOM, swaps every
 * dictionary-matching text node, and starts an observer so future renders
 * also get translated. Idempotent — safe to call repeatedly with the same
 * language (it will re-scan for newly-rendered content).
 *
 * Multi-pass walking: many components in this codebase are React.lazy and
 * mount AFTER the initial render (Footer, FloatingButtons, BookingModal,
 * CountdownBanner, etc.). The MutationObserver SHOULD catch them as they
 * mount, but we also schedule extra walks at 500ms/1500ms/3500ms as belt-
 * and-suspenders for components that hydrate via intersection observer
 * (whileInView from framer-motion) or React.Suspense boundaries that
 * resolve slowly on cold caches.
 */
export function applyLanguage(lang) {
  if (typeof document === 'undefined' || !document.body) return;
  if (lang === activeLang && observer) {
    // Same lang already active — re-walk to pick up newly-rendered nodes.
    walkAndTranslate(document.body);
    return;
  }
  activeLang = lang;

  if (lang === 'en') {
    // Going back to source — disconnect observer and revert all originals.
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    restoreAllOriginals(document.body);
    setActiveLanguage('en');
    return;
  }

  // Tell the AI translator which language is active (used for cache-key
  // namespacing and queue invalidation when user switches mid-batch).
  setActiveLanguage(lang);

  // Initial walk.
  walkAndTranslate(document.body);

  // Multi-pass: re-walk after delays to catch lazy-loaded / late-mounting
  // components (Footer is React.lazy, etc.). Cheap to repeat since the
  // walker is O(text-nodes-in-DOM) and most nodes will already be in the
  // correct language (translateTextNode bails early if value matches).
  [500, 1500, 3500].forEach((delay) => {
    setTimeout(() => {
      if (activeLang === lang) walkAndTranslate(document.body);
    }, delay);
  });

  // Start observer for future React renders / SPA navigation.
  if (!observer) {
    observer = new MutationObserver(handleMutations);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      characterDataOldValue: false,
    });
  }
}

/** Optional cleanup if the I18nProvider ever unmounts (rare in SPAs). */
export function stopTranslator() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  activeLang = 'en';
}
